import { GetStaticPropsResult } from 'next'
import React, { ReactElement } from 'react'
import { PageMeta } from 'src/pages'
import { GlassesOutline } from 'react-ionicons'
import { Layout } from '@/components/Layout'
import homeData from '@/content/home.json'
import { getMovies } from '@/src/movies'
import { MovieHeader } from '@/components/patterns/MovieHeader'
import { MovieData } from '@/components/patterns/MovieCard'
import { Container, Section } from '@/components/utils'
import { MovieCarousel } from '@/components/patterns/MovieCarousel'
import { getCinemas } from '@/src/cinemas'
import { Cinema, Movie } from '@/src/types'
import {
  NewsletterSection,
  NewsletterSectionProps,
} from '@/components/patterns/NewsletterSection'
import { IonIcon } from '@/src/icons'
import dynamic from 'next/dynamic'

const MovieCinemas = dynamic(() => import('@/components/patterns/MovieCinemas').then(module => module.MovieCinemas), { 
  ssr:false 
})

interface MoviePageProps {
  // eslint-disable-next-line react/no-unused-prop-types
  meta: PageMeta
  cinemas: Cinema[]
  movie: Movie
  newsletter: NewsletterSectionProps
}

export async function getStaticProps({
  params,
}): Promise<GetStaticPropsResult<MoviePageProps>> {
  const movies = await getMovies()
  const allCinemas = await getCinemas()

  const cinemaIds = movies.flatMap((movie) =>
    movie.projections.map((projection) => projection.cinema)
  )
  const cinemas = allCinemas.filter((cinema) => cinemaIds.includes(cinema.id))

  const currentMovie = movies.find((film) => {
    const mov =
      film.earliestProjection && film.earliestProjection.date
        ? film.earliestProjection.date
        : '0'
    return (
      new Date(mov).getMonth() === new Date().getMonth() &&
      new Date(mov).getFullYear() === new Date().getFullYear()
    )
  })

  const promotedMovie = movies.find(
    (movie) => movie.id === homeData.promotedMovie
  )

  const movie = promotedMovie ?? currentMovie

  return {
    props: {
      meta: {
        title: movie ? movie.title : null,
        description: movie ? movie.authorship ?? null : null,
      },
      ...homeData,
      movie: movie || null,
      cinemas,
    },
  }
}

export default function ProperamentPage({
  meta,
  movie,
  cinemas,
  newsletter,
}: MoviePageProps): ReactElement {
  return movie ? (
    <Layout meta={meta}>
      <MovieHeader movie={movie} />
      <Container>
        <Section>
          <MovieData movie={movie} showMovieLink={false} />
        </Section>
      </Container>
      {movie.slides && <MovieCarousel slides={movie.slides} />}
      <div id="sala" />
      <MovieCinemas cinemas={cinemas} movie={movie} />
      <NewsletterSection {...newsletter} />
    </Layout>
  ) : (
    <Layout meta={meta}>
      <Container>
        <Section>
          <div className="flex flex-col items-center py-2 text-xl">
            <IonIcon className="mr-2 w-32" icon={GlassesOutline} />
            <h3 className="text-center text-6xl font-black leading-none">
              Sense Pel·lícules Actualment
            </h3>
          </div>
        </Section>
      </Container>
      <NewsletterSection {...newsletter} />
    </Layout>
  )
}
